import React from "react";
import PropTypes from "prop-types";

import { useBreakpoint } from "../../../hooks/useBreakpoint";
import { stripHtmlTags, uniqBy } from "../../../utils/commonFunctions";
import { Link } from "../../../utils/customLink";

import { ConditionalSwiper } from "../../shared/conditionalSwiper";
import CollapsibleListWrapper from "../../shared/collapsibleListWrapper";
import CollapsibleWrapper from "../../shared/collapsibleWrapper";
import ExpeditionCard from "../../cards/expeditionsCard";
import ExperienceCard from "../../base/card/experienceCard";
import TitleTextBLockCard from "../../cards/titleTextBlockCard";

const Overview = ({
  projectOverview,
  programTypes,
  programFocus,
  programLocation,
  groupProjectOverview,
  programHighlights,
  programSkills,
  programActivities,
  programPartners,
  programSectionTitles,
  programGeoSlugs,
  groupExpeditions,
  groupExpExpeditionsMainTitle,
  groupExpeditionsMainDescription
}) => {
  const breakpoint = useBreakpoint();
  const programTypeCollection = programTypes?.nodes ?? programTypes ?? [];

  const programFocuses =
    programFocus && programFocus?.length
      ? programFocus
          ?.filter((item) => item.title)
          ?.map((focus) => {
            return {
              text: focus.title,
              toPage: focus?.programFocus?.focusPageLink?.uri ?? "#",
              forMe:
                focus?.programFocus?.programForMeList?.flatMap((item) => {
                  return { title: item.pfmTitle?.trim() };
                }) ?? []
            };
          })
      : [];

  const programType =
    programTypeCollection && programTypeCollection?.length
      ? programTypeCollection
          ?.filter((item) => item.title)
          ?.map((pgmType) => {
            return {
              text: pgmType?.title,
              slug: pgmType?.slug,
              toPage: pgmType?.acfProgramsType?.pageLink?.uri ?? "#",
              fieldworkHours: pgmType?.acfProgramsType?.fieldworkHours
            };
          })
      : [];

  const groupProgramType =
    programTypeCollection && programTypeCollection?.length
      ? programTypeCollection
          ?.filter((item) => item.name)
          ?.map((pgmType) => {
            return {
              text: pgmType.name,
              slug: pgmType.slug,
              toPage: pgmType?.acfProgramsType?.pageLink?.uri ?? "#",
              fieldworkHours: pgmType?.acfProgramsType?.fieldworkHours
            };
          })
      : [];

  const pgmHighlights =
    programHighlights
      ?.filter((highlight) => highlight?.pgmHighlightTitle)
      ?.map((item, idx) => {
        return {
          experienceTitle: item?.pgmHighlightTitle,
          bgImage: item?.pgmHighlightImage.gatsbyImage,
          experienceDescription: stripHtmlTags(item?.pgmHighlightDesc),
          fallbackImage: item?.pgmHighlightImage?.sourceUrl,
          descriptionColor: "text-generic-text planet:gvi-light-gray-text",
          iconImage: null,
          isRounded: true,
          truncateDesc: false,
          useKey: `highlight-key-${idx}`
        };
      }) ?? [];

  const productTypeHighlights =
    programTypeCollection && programTypeCollection?.length
      ? programTypeCollection
          ?.filter((item) => item?.title)
          ?.flatMap(
            (pgmType) => pgmType?.acfProgramsType?.programTypeHighlights
          )
          ?.filter((highlight) =>
            highlight?.programTypeHub?.some((thehub) =>
              programGeoSlugs.includes(thehub.slug)
            )
          )
          ?.flatMap(
            (highlightsCollection) =>
              highlightsCollection?.programTypeHubHighlights
          )
      : [];

  const typeHighlights =
    productTypeHighlights?.map((highlight, idx) => {
      return {
        experienceTitle: highlight?.programTypeHighlightsTitle,
        bgImage: highlight?.programTypeHighlightsImage?.gatsbyImage,
        experienceDescription: stripHtmlTags(
          highlight?.programTypeHighlightsDesc
        ),
        fallbackImage: highlight?.programTypeHighlightsImage?.sourceUrl,
        descriptionColor: "text-generic-text planet:gvi-light-gray-text",
        iconImage: null,
        isRounded: true,
        truncateDesc: false,
        useKey: `product-type-highlight-key-${idx}`
      };
    }) || [];

  const combinedHighlights = pgmHighlights.concat(typeHighlights);

  const activities = programActivities
    ?.filter((item) => item?.pgmActivityTitle)
    ?.map((item, idx) => {
      return {
        experienceTitle: item?.pgmActivityTitle,
        bgImage: item?.pgmActivityImage?.gatsbyImage,
        experienceDescription: stripHtmlTags(item?.pgmActivityDesc),
        fallbackImage: item?.pgmActivityImage?.sourceUrl,
        descriptionColor: "text-generic-text planet:gvi-light-gray-text",
        iconImage: null,
        isRounded: true,
        truncateDesc: false,
        useKey: `activity-key-${idx}`
      };
    });

  const pgmSkills =
    programSkills
      ?.filter((item) => item?.name)
      ?.map((item, idx) => {
        return { title: item?.name, useKey: `skill-key-${idx}` };
      }) ?? [];

  const productTypeSkills =
    programTypeCollection && programTypeCollection?.length
      ? programTypeCollection
          ?.filter((item) => item?.title)
          ?.flatMap((pgmType) => pgmType?.acfProgramsType?.programTypeSkill)
      : [];

  const typeSkills =
    productTypeSkills
      ?.filter((item) => item?.programTypeSkillTitle)
      ?.map((item, idx) => {
        return {
          title: item?.programTypeSkillTitle,
          useKey: `skill-key-${idx}`
        };
      }) || [];

  const combinedSkills = pgmSkills.concat(typeSkills);

  const partners =
    programPartners
      ?.filter((item) => item?.name)
      ?.map((item, idx) => {
        return {
          text: item?.name,
          useKey: `partners-key-${idx}`
        };
      }) ?? [];

  const fieldHours =
    programType?.length > 0
      ? programType[0]?.fieldworkHours
      : groupProgramType?.length > 0
        ? groupProgramType[0]?.fieldworkHours
        : null;

  const internshipFilterSetList = [
    "internship",
    "professional-apprenticeship",
    "research-fellowship"
  ];
  const isInternship =
    programType?.length > 0
      ? programType
          ?.flatMap((item) => item.slug)
          ?.some((filterByItem) =>
            internshipFilterSetList.includes(filterByItem)
          )
      : groupProgramType?.length > 0
        ? groupProgramType
            ?.flatMap((item) => item.slug)
            ?.some((filterByItem) =>
              internshipFilterSetList.includes(filterByItem)
            )
        : false;

  const isProgramForMeCombinedList =
    programFocuses && programFocuses?.length > 0
      ? programFocuses?.flatMap((item) => item?.forMe)
      : [];
  const programForMe = uniqBy(isProgramForMeCombinedList, "title");

  const overviewDescLength =
    pgmHighlights?.length > 0 ||
    activities?.length > 0 ||
    groupExpeditions?.length > 0
      ? 350
      : 5000;

  const sectionHeading = {
    ...programSectionTitles,
    overview: "Program overview",
    details: "Program details",
    program_type: "Program type",
    location: "Location",
    focus: "Focus",
    skills: "Skills"
  };

  return (
    <>
      <h2 className="heading text-left py-5">{sectionHeading.overview}</h2>

      {fieldHours && (
        <div className="fieldwork pt-3 pb-8">
          <div
            className={
              "border-0 border-l-4 border-red-light planet:border-green-standard pl-2 text-generic-text planet:text-white font-lato font-normal text-sm"
            }
          >
            {fieldHours} hours of fieldwork per week
          </div>
        </div>
      )}

      <div className="divide-y width-divider" />

      <CollapsibleWrapper
        desc={projectOverview ?? groupProjectOverview}
        descStyle=""
        btnStyle="underline underline-offset-2 pt-2 text-generic-text font-lato planet:font-normal planet:text-white text-sm flex items-center gap-2"
        wrapperContainer="planet:gvi-light-gray-text text-black-lighter text-sm pt-5 tab-content_html"
        visibleLines={3}
        visibleLength={230}
        type={"both"}
        displayFullDesc={true}
      />

      {groupProgramType?.length > 0 && groupExpeditions?.length > 0 && (
        <ExpeditionCard
          groupExpeditions={groupExpeditions}
          groupExpExpeditionsMainTitle={groupExpExpeditionsMainTitle}
          groupExpeditionsMainDescription={groupExpeditionsMainDescription}
        />
      )}

      {/* pgmHighlights */}
      {combinedHighlights?.length > 0 && (
        <>
          <div className="divide-y width-divider py-4 sm:hidden" />
          <h3
            className="heading py-5 font-alice font-normal text-black-lighter planet:font-body planet:font-light planet:text-white"
            dangerouslySetInnerHTML={{
              __html: sectionHeading?.highlightSectionTitle ?? "Highlights"
            }}
          />
          <div
            className="planet:gvi-light-gray-text text-black-lighter"
            dangerouslySetInnerHTML={{
              __html: sectionHeading?.highlightSectionDesc ?? ""
            }}
          />
          <div className="sm:grid sm:grid-cols-2 gap-x-8 gap-y-6 py-2">
            <ConditionalSwiper
              condition={breakpoint === "xs"}
              slidesPerView={1.25}
              spaceBetween={25}
              centeredSlides={false}
              className="h-full w-full"
              visibleLength={overviewDescLength}
            >
              {combinedHighlights?.map((item, idx) => (
                <ExperienceCard
                  key={`product-type-highlight-key-${idx}`}
                  {...item}
                />
              ))}
            </ConditionalSwiper>
          </div>
        </>
      )}

      {isInternship && programForMe?.length > 0 && (
        <>
          <div className="divide-y width-divider py-4 sm:hidden" />
          <h3 className="heading py-5 font-alice font-normal text-black-lighter planet:font-body planet:font-light planet:text-white">
            Is this program for me?
          </h3>
          <div
            className="planet:gvi-light-gray-text text-black-lighter"
            dangerouslySetInnerHTML={{
              __html: `<p>This internship is specifically useful for someone who has or is actively studying the below subject areas at school, university or college, or has an interest in these subject areas.</p>`
            }}
          />
          <div className="grid grid-cols-1 gap-0 py-2">
            <CollapsibleListWrapper
              visibleItems={50}
              listItems={programForMe}
            />
          </div>
        </>
      )}

      {activities?.length > 0 && (
        <>
          <div className="divide-y width-divider py-4 sm:hidden" />
          <h3
            className="heading py-5 font-alice font-normal text-black-lighter planet:font-body planet:font-light planet:text-white"
            dangerouslySetInnerHTML={{
              __html: sectionHeading?.activitySectionTitle ?? "Activities"
            }}
          />
          <div
            className="planet:gvi-light-gray-text text-black-lighter"
            dangerouslySetInnerHTML={{
              __html: sectionHeading?.activitySectionDesc ?? ""
            }}
          />
          <div className="sm:grid sm:grid-cols-2 gap-x-8 gap-y-6 py-2">
            <ConditionalSwiper
              condition={breakpoint === "xs"}
              slidesPerView={1.25}
              spaceBetween={25}
              centeredSlides={false}
              className="h-full w-full"
            >
              {activities?.map((item, idx) => (
                <ExperienceCard key={`activity-key-${idx}`} {...item} />
              ))}
            </ConditionalSwiper>
          </div>
        </>
      )}

      {combinedSkills?.length > 0 && (
        <>
          <div className="divide-y width-divider py-4 sm:hidden" />
          <h3 className="heading py-5 font-alice font-normal text-black-lighter planet:font-body planet:font-light planet:text-white">
            Skills
          </h3>
          <div className="grid grid-cols-1 gap-0 py-2">
            <CollapsibleListWrapper
              visibleItems={6}
              listItems={combinedSkills}
            />
          </div>

          {/*<div className="planet:gvi-light-gray-text text-black-lighter py-2">*/}
          {/*  For more information on the pgmSkills and training involved, please see*/}
          {/*  our{" "}*/}
          {/*  <Link*/}
          {/*    to="?tab=qualifications-and-training"*/}
          {/*    title={"Go to training and qualifications section"}*/}
          {/*  >*/}
          {/*    training and qualifications section*/}
          {/*  </Link>*/}
          {/*</div>*/}
        </>
      )}

      {partners && partners?.length > 0 && (
        <>
          <div className="divide-y width-divider py-4 sm:hidden" />
          <h3 className="heading py-5 font-alice font-normal text-black-lighter planet:font-body planet:font-light planet:text-white">
            Partners
          </h3>
          <div
            className="planet:gvi-light-gray-text text-black-lighter"
            dangerouslySetInnerHTML={{
              __html:
                sectionHeading?.partnerSectionDesc ??
                "<p>Some of the partners we work with on base.</p>"
            }}
          />
          <div className="sm:grid sm:grid-cols-2 gap-4 py-2">
            <ConditionalSwiper
              condition={breakpoint === "xs"}
              slidesPerView={1.25}
              spaceBetween={25}
              centeredSlides={false}
              className="h-full w-full"
            >
              {partners?.map((item, idx) => (
                <TitleTextBLockCard key={`partners-key-${idx}`} {...item} />
              ))}
            </ConditionalSwiper>
          </div>
        </>
      )}

      <div className="divide-y width-divider py-4 sm:hidden" />
      <h3 className="heading text-left pt-6 !pb-8 font-alice font-normal text-black-lighter planet:font-body planet:font-light planet:text-white">
        {sectionHeading.details}
      </h3>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 divide-x divide-gray-senary-light planet:divide-gray-senary-dark sm:divide-x p-5 bg-gray-lighten planet:gvi-light-gray-bg rounded-md planet:rounded-none shadow-md planet:shadow-none">
        <div className="px-5">
          <div
            className="planet:gvi-light-gray-text  text-black-lighter text-sm pb-2"
            dangerouslySetInnerHTML={{
              __html: sectionHeading.program_type
            }}
          />

          <div className="flex flex-col">
            {programTypes?.length > 0 && (
              <>
                {programType?.map((pType, idx) => (
                  <React.Fragment key={idx}>
                    <Link
                      to={pType.toPage}
                      className="font-semi-bold text-generic-text active:text-generic-text hover:text-generic-text h8 underline-offset-2 pb-1 planet:text-white underline"
                    >
                      {pType.text} {idx === programType.length - 1 ? " " : ", "}
                    </Link>
                  </React.Fragment>
                ))}
              </>
            )}
            {groupProgramType?.length > 0 && (
              <>
                {groupProgramType?.map((gpType, idx) => (
                  <React.Fragment key={idx}>
                    <Link
                      to={gpType.toPage}
                      className="font-semi-bold text-generic-text active:text-generic-text hover:text-generic-text h8 underline-offset-2 pb-1 planet:text-white underline"
                    >
                      {gpType.text}{" "}
                      {idx === groupProgramType.length - 1 ? " " : ", "}
                    </Link>
                  </React.Fragment>
                ))}
              </>
            )}
          </div>
        </div>

        {programLocation && programLocation?.length > 0 && (
          <div className="px-5">
            <div className="planet:gvi-light-gray-text  text-black-lighter text-sm pb-2">
              {sectionHeading.location}
            </div>
            <div className="flex flex-col">
              {programLocation?.map((locationItem, idx) => {
                return (
                  <React.Fragment key={idx}>
                    <Link
                      to={locationItem.toPage}
                      className="font-semi-bold text-generic-text active:text-generic-text hover:text-generic-text h8 underline-offset-2 pb-1 planet:text-white underline"
                    >
                      {locationItem.text}{" "}
                      {idx === programLocation.length - 1 ? " " : ", "}
                    </Link>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}

        {programFocuses && programFocuses?.length > 0 && (
          <div className="col-span-full sm:col-span-1 px-5 pt-5 mt-5 border-0 border-t sm:border-t-0 sm:pt-0 !border-l-0 sm:!border-l sm:mt-0">
            <div className="planet:gvi-light-gray-text  text-black-lighter  text-sm pb-2">
              {sectionHeading.focus}
            </div>
            {programFocuses?.map((focus, idx) => (
              <React.Fragment key={idx}>
                <Link
                  to={focus.toPage}
                  className="font-semi-bold text-generic-text active:text-generic-text hover:text-generic-text h8 underline-offset-2 pb-1 planet:text-white underline"
                >
                  {focus.text} {idx === programFocuses.length - 1 ? " " : ", "}
                </Link>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

Overview.defaultProps = {
  programHighlights: [],
  programSkills: [],
  programActivities: [],
  programPartners: [],
  programSectionTitles: {}
};

Overview.propTypes = {
  programHighlights: PropTypes.array,
  programSkills: PropTypes.array,
  programActivities: PropTypes.array,
  programPartners: PropTypes.array,
  programSectionTitles: PropTypes.object
};
export default Overview;
